/**
 * DropdownItem component - Horizontal layout item for dropdowns
 * Factory function following the app's architectural patterns
 */

import { createIcon, type IconAPI } from "@UI/Icon/index.js";

export interface DropdownItemConfig {
  readonly icon?: string;
  readonly label: string;
  readonly disabled?: boolean;
  readonly selected?: boolean;
  readonly showControl?: boolean;
  readonly controlContent?: HTMLElement;
  readonly className?: string;
  readonly onClick?: () => void;
}

export interface DropdownItemAPI {
  readonly getElement: () => HTMLElement;
  readonly setLabel: (label: string) => void;
  readonly setIcon: (iconName: string | null) => void;
  readonly setDisabled: (disabled: boolean) => void;
  readonly setSelected: (selected: boolean) => void;
  readonly setControlContent: (content: HTMLElement | null) => void;
  readonly destroy: () => void;
}

/**
 * Creates DropdownItem component with icon, label, and optional control
 * @param config - Configuration for the dropdown item
 * @returns DropdownItem API
 */
export const createDropdownItem = (
  config: DropdownItemConfig
): DropdownItemAPI => {
  const {
    icon,
    label,
    disabled = false,
    selected = false,
    showControl = false,
    controlContent,
    className = "",
    onClick,
  } = config;

  let isDestroyed = false;
  let isDisabled = disabled;
  let isSelected = selected;
  let iconAPI: IconAPI | null = null;

  // Create main container
  const container = document.createElement("div");
  container.className = `dropdown-item ${className}`.trim();

  // Create icon container (optional)
  const iconContainer = document.createElement("div");
  iconContainer.className = "dropdown-item__icon";

  // Create label element
  const labelElement = document.createElement("div");
  labelElement.className = "dropdown-item__label";
  labelElement.textContent = label;

  // Create control container (optional)
  const controlContainer = document.createElement("div");
  controlContainer.className = "dropdown-item__control";

  /**
   * Initialize icon if provided
   */
  const initIcon = (): void => {
    if (!icon) return;

    try {
      iconAPI = createIcon({
        name: icon,
        size: 24,
        useContainer: true,
      });
      iconContainer.appendChild(iconAPI.getElement());
    } catch (error) {
      console.error(`Failed to create icon "${icon}" for DropdownItem:`, error);
    }
  };

  /**
   * Update container state classes
   */
  const updateState = (): void => {
    if (isDestroyed) return;

    container.classList.toggle("dropdown-item--disabled", isDisabled);
    container.classList.toggle("dropdown-item--selected", isSelected);
    container.classList.toggle("dropdown-item--has-icon", !!iconAPI);
    container.classList.toggle("dropdown-item--has-control", showControl);
  };

  /**
   * Handle click events
   */
  const handleClick = (): void => {
    if (isDisabled || isDestroyed) return;
    onClick?.();
  };

  /**
   * Set label text
   */
  const setLabel = (newLabel: string): void => {
    if (isDestroyed) return;
    labelElement.textContent = newLabel;
  };

  /**
   * Set or remove icon
   */
  const setIcon = (iconName: string | null): void => {
    if (isDestroyed) return;

    // Destroy existing icon
    if (iconAPI) {
      iconAPI.destroy();
      iconAPI = null;
    }

    // Create new icon if provided
    if (iconName) {
      try {
        iconAPI = createIcon({
          name: iconName,
          size: 24,
          useContainer: true,
        });
        iconContainer.appendChild(iconAPI.getElement());
      } catch (error) {
        console.error(
          `Failed to create icon "${iconName}" for DropdownItem:`,
          error
        );
      }
    }

    updateState();
  };

  /**
   * Set disabled state
   */
  const setDisabled = (newDisabled: boolean): void => {
    if (isDestroyed) return;
    isDisabled = newDisabled;
    updateState();
  };

  /**
   * Set selected state
   */
  const setSelected = (newSelected: boolean): void => {
    if (isDestroyed) return;
    isSelected = newSelected;
    updateState();
  };

  /**
   * Set control content
   */
  const setControlContent = (content: HTMLElement | null): void => {
    if (isDestroyed) return;

    // Clear existing content
    controlContainer.innerHTML = "";

    // Add new content if provided
    if (content) {
      controlContainer.appendChild(content);
    }

    updateState();
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Remove event listeners
    container.removeEventListener("click", handleClick);

    // Destroy icon
    iconAPI?.destroy();
    iconAPI = null;

    // Remove from DOM if still connected
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  // Build the DOM structure
  container.appendChild(iconContainer);
  container.appendChild(labelElement);

  if (showControl) {
    container.appendChild(controlContainer);
    if (controlContent) {
      controlContainer.appendChild(controlContent);
    }
  }

  // Initialize components
  initIcon();

  // Set up event listeners
  container.addEventListener("click", handleClick);

  // Apply initial state
  updateState();

  return {
    getElement: () => container,
    setLabel,
    setIcon,
    setDisabled,
    setSelected,
    setControlContent,
    destroy,
  };
};

/**
 * Creates DropdownItem component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the dropdown item
 * @returns DropdownItem API
 */
export const createDropdownItemInContainer = (
  container: HTMLElement,
  config: DropdownItemConfig
): DropdownItemAPI => {
  const dropdownItem = createDropdownItem(config);
  container.appendChild(dropdownItem.getElement());
  return dropdownItem;
};
